import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownloadOutlined,
  UnlockOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Popconfirm, Space, Table, Tag, message } from 'antd';
import { API } from '@/services/api.service';

const STATUS_COLOR = {
  DRAFT: 'default',
  VALIDATED: 'blue',
  GENERATED: 'green',
  SUBMITTED: 'gold',
};

/**
 * Every period produced so far, and the one action that changes a period's
 * meaning: marking it as sent, after which it can no longer be rewritten.
 *
 * @param {Object} props - Component props
 * @param {string} props.packageId - Package whose runs are listed
 * @returns {JSX.Element} The runs table
 */
export default function RunsListPanel({ packageId }) {
  const { t } = useTranslation(['tables', 'messages']);
  const gr = (key) => t(`groupReporting.${key}`, { ns: 'tables' });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['group-reporting-runs', packageId],
    queryFn: () => API.groupReporting.getRuns(packageId ? { packageId } : {}),
  });

  const runs = data?.data || [];

  const downloadMutation = useMutation({
    mutationFn: (runId) => API.groupReporting.downloadRun(runId),
    onSuccess: (result) => {
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || error.message),
  });

  const reopenMutation = useMutation({
    mutationFn: (runId) => API.groupReporting.reopenRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['group-reporting-runs', packageId],
      });
      message.success(t('updateSuccess', { ns: 'messages' }));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (runId) => API.groupReporting.deleteRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['group-reporting-runs', packageId],
      });
      message.success(t('deleteSuccess', { ns: 'messages' }));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || error.message),
  });

  const submitMutation = useMutation({
    mutationFn: (runId) => API.groupReporting.submitRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['group-reporting-runs', packageId],
      });
      message.success(t('updateSuccess', { ns: 'messages' }));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || error.message),
  });

  const columns = [
    {
      title: gr('period'),
      key: 'period',
      render: (_, row) => `${row.year}-${String(row.month).padStart(2, '0')}`,
      width: 110,
    },
    {
      title: gr('status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Tag color={STATUS_COLOR[status]}>{status}</Tag>,
    },
    { title: gr('fxRate'), dataIndex: 'fxRate', key: 'fxRate', width: 110 },
    {
      title: gr('amountTarget'),
      key: 'targetAmount',
      align: 'right',
      render: (_, row) =>
        Number(row.totals?.targetAmount || 0).toLocaleString('sr-RS', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: gr('findings'),
      key: 'findings',
      width: 110,
      render: (_, row) => {
        const count = row.validations?.length || 0;
        if (!count) return <Tag color="green">0</Tag>;
        const blocking = row.validations.some(
          (finding) => finding.severity === 'ERROR'
        );
        return <Tag color={blocking ? 'red' : 'orange'}>{count}</Tag>;
      },
    },
    {
      title: gr('generatedAt'),
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      render: (value) =>
        value ? new Date(value).toLocaleString('sr-RS') : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 330,
      render: (_, row) => {
        // A submitted period is the record of what the group received: it can be
        // reopened, but never deleted. Anything else is still a draft.
        const isSubmitted = row.status === 'SUBMITTED';

        return (
          <Space>
            <Button
              size="small"
              className="!rounded-[var(--buttonRadius)]"
              icon={<DownloadOutlined />}
              // A run produced before the store was reachable has nothing to hand back.
              disabled={!row.generatedFileKey}
              loading={
                downloadMutation.isPending &&
                downloadMutation.variables === row._id
              }
              onClick={() => downloadMutation.mutate(row._id)}
            >
              {gr('download')}
            </Button>

            {row.status === 'GENERATED' && (
              <Popconfirm
                title={gr('confirmSubmit')}
                onConfirm={() => submitMutation.mutate(row._id)}
              >
                <Button size="small" className="!rounded-[var(--buttonRadius)]">
                  {gr('submitRun')}
                </Button>
              </Popconfirm>
            )}

            {isSubmitted && (
              <Popconfirm
                title={gr('confirmReopen')}
                onConfirm={() => reopenMutation.mutate(row._id)}
              >
                <Button
                  size="small"
                  className="!rounded-[var(--buttonRadius)]"
                  icon={<UnlockOutlined />}
                  loading={
                    reopenMutation.isPending &&
                    reopenMutation.variables === row._id
                  }
                >
                  {gr('reopenRun')}
                </Button>
              </Popconfirm>
            )}

            {!isSubmitted && (
              <Popconfirm
                title={gr('confirmDeleteRun')}
                onConfirm={() => deleteMutation.mutate(row._id)}
              >
                <Button
                  size="small"
                  danger
                  className="!rounded-[var(--buttonRadius)]"
                  icon={<DeleteOutlined />}
                  loading={
                    deleteMutation.isPending &&
                    deleteMutation.variables === row._id
                  }
                />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card size="small">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Table
          size="small"
          rowKey="_id"
          loading={isLoading}
          columns={columns}
          dataSource={runs}
          locale={{ emptyText: gr('noRuns') }}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
        />
      </Space>
    </Card>
  );
}
